import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { certificatesService, CertificateListParams, RevokeCertificatePayload, UploadCertificatePayload } from '@/services/certificatesService'
import { CertificateModel } from '@/models/certificate.model'

const certificateKeys = {
  all: ['certificates'] as const,
  list: (paramsKey: (string | number | boolean | null)[]) => [...certificateKeys.all, 'list', ...paramsKey] as const,
  detail: (certificateId: string) => [...certificateKeys.all, 'detail', certificateId] as const,
}

export interface UseCertificateListParams extends CertificateListParams {}

export function useCertificates(params: UseCertificateListParams) {
  const queryParams = useMemo(() => {
    const sanitized: CertificateListParams = {
      page: params.page ?? 1,
      perPage: params.perPage ?? 10,
    }

    if (params.userId) sanitized.userId = params.userId
    if (params.type) sanitized.type = params.type
    if (params.programmeId) sanitized.programmeId = params.programmeId
    if (params.courseId) sanitized.courseId = params.courseId

    return sanitized
  }, [params.page, params.perPage, params.userId, params.type, params.programmeId, params.courseId])

  const key = [
    queryParams.page ?? 1,
    queryParams.perPage ?? 10,
    queryParams.userId ?? '',
    queryParams.type ?? '',
    queryParams.programmeId ?? '',
    queryParams.courseId ?? '',
  ]

  return useQuery({
    queryKey: certificateKeys.list(key),
    queryFn: () => certificatesService.getCertificates(queryParams),
    placeholderData: (previousData) => previousData,
    staleTime: 60 * 1000,
  })
}

export function useCertificate(certificateId: string | undefined) {
  return useQuery({
    queryKey: certificateId ? certificateKeys.detail(certificateId) : certificateKeys.all,
    queryFn: () => (certificateId ? certificatesService.getCertificateById(certificateId) : Promise.reject(new Error('Certificate ID is required'))),
    enabled: Boolean(certificateId),
    staleTime: 60 * 1000,
  })
}

export function useUploadCertificate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: UploadCertificatePayload) => certificatesService.uploadCertificate(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: certificateKeys.all })
    },
  })
}

export function useRevokeCertificate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ certificateId, payload }: { certificateId: string; payload: RevokeCertificatePayload }) =>
      certificatesService.revokeCertificate(certificateId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: certificateKeys.all })
    },
  })
}

export function useVerifyCertificate() {
  return useMutation({
    mutationFn: ({ certificateNumber, token }: { certificateNumber: string; token?: string }) =>
      certificatesService.verifyCertificate(certificateNumber, token),
  })
}
